import type { ChangeEvent, FunctionComponent, InputHTMLAttributes } from 'react';
import { useId } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useModifiers } from '../../../styles/hooks/useModifiers';

import styles from './Toggle.module.css';

export enum ToggleScale {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}
export enum ToggleVariant {
  MAIN = 'main',
  ACCENT = 'accent',
}

export interface ToggleProps extends InputHTMLAttributes<HTMLInputElement> {
  /** The value by which the component scales. Default is 1 (no scale) */
  scale?: ToggleScale;
  withLabel?: boolean;
  variant?: ToggleVariant;
}

export const Toggle: FunctionComponent<ToggleProps> = ({
  checked,
  disabled,
  id,
  onChange,
  scale = 'medium',
  withLabel,
  variant = ToggleVariant.MAIN,
  ...rest
}) => {
  const audioTrigger = useAudioTrigger();
  const reactCreatedId = useId();
  const anchorId = id || reactCreatedId;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(event);
    audioTrigger.play({ name: 'menuClick' });
  };

  const mods = useModifiers(
    styles,
    'toggle',
    {
      [scale]: true,
      disabled,
      withLabel,
      accent: variant === ToggleVariant.ACCENT,
    },
    { withBaseClass: true }
  );

  return (
    <div data-testid="toggle" className={mods}>
      <input
        className={styles.toggle__input}
        checked={checked}
        disabled={disabled}
        id={anchorId}
        onChange={handleChange}
        type="checkbox"
        {...rest}
      />
      <label htmlFor={anchorId} className={styles.toggle__track}>
        <span className={styles.toggle__handle} />
      </label>
    </div>
  );
};
